/**
 * AiDrawer — the V2 PRATUS AI shell (flag `AI_CHAT_V2`). Resizable right-hand
 * drawer on desktop (width persisted), full-screen Sheet below `md`. Header:
 * AI/Notes panel toggle, agent mode (AI only), close. Session picker sits on a
 * second row so it does not crowd the mode toggle. Body: chat or Notes. Footer:
 * export/copy/delete for saved chats. HITL via `ReviewQueueModal`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  ClipboardCopyIcon,
  FileDownIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useIsMobile } from 'hooks/use-mobile';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';
import { setCurrentSitrepId, setSelectedDChat } from 'slices/dchatSlice';
import { observability } from 'utils/observability';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import { NotesView } from 'components/Notes/NotesView';

import ReviewQueueModal from '../agentConfirm/ReviewQueueModal';
import { docxExport } from '../DisasterGPTDataHelpers/DGPTExport';
import {
  useDeleteDisasterChat,
  useDisasterChats,
  useUpdateDisasterChat,
} from '../hooks/useDisasterChats';
import { useAICatalog } from '../hooks/useAICommands';
import { useFetchCitationFileUrl } from '../hooks/useFetchCitationFileUrl';
import AiPanelToggle from './AiPanelToggle';
import Composer from './Composer';
import EmptyState from './EmptyState';
import MessageList from './MessageList';
import { cleanMessageContent } from './MessageBubble';
import ModeToggle from './ModeToggle';
import SessionPicker, { type ChatSessionSummary } from './SessionPicker';
import { useAiWorkspaceIncident } from './useAiWorkspaceIncident';
import { useChatSession } from './useChatSession';
import { useDrawerWidth } from './useDrawerWidth';
import type { ChatMessage } from './types';

/**
 * `docxExport` is JSDoc-typed as taking an array, but the legacy chat also
 * passes a plain string for per-message clipboard copies. Mirror that here.
 */
const exportDocx = docxExport as unknown as (
  data: ChatMessage[] | string,
  destination: 'Download' | 'Clipboard',
  filename: string
) => Promise<void>;

export interface AiDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Datetime window passed through to the agent (legacy prop). */
  selectedDatetime?: string;
}

interface DrawerBodyProps {
  open: boolean;
  onClose: () => void;
  selectedDatetime?: string;
  /** Desktop drag handle; omitted in the mobile sheet. */
  resizeHandle?: React.ReactNode;
}

const DrawerBody: React.FC<DrawerBodyProps> = ({
  open,
  onClose,
  selectedDatetime,
  resizeHandle,
}) => {
  const dispatch = useDispatch();
  const session = useChatSession({ selectedDatetime, onClose });
  const {
    composerDraftLabel,
    clearComposerDraft,
    aiPanel,
    setAiPanel,
    notesFeedbackActive,
    queuedOpenNoteId,
    consumeQueuedOpenNoteId,
  } = useChatSidebar();
  const incident = useAiWorkspaceIncident();
  const group = useAppSelector(selectGroup) as
    | { group_name?: string }
    | undefined;
  const { data: catalog } = useAICatalog();
  const fetchFileUrl = useFetchCitationFileUrl();

  const { data: chats = [], isLoading: chatsLoading } = useDisasterChats() as {
    data?: ChatSessionSummary[];
    isLoading: boolean;
  };
  const updateChat = useUpdateDisasterChat();
  const deleteChat = useDeleteDisasterChat();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Closing the review hides it for this batch only; the chat stays open and
  // the banner below reopens it. A new batch shows the modal again.
  const [dismissedReview, setDismissedReview] = useState<unknown>(null);
  const hasPendingReview = session.pendingConfirmations.length > 0;
  const reviewDismissed = dismissedReview === session.pendingConfirmations;

  // Default to AI when the drawer opens, unless Notes feedback / panel intent is active.
  useEffect(() => {
    if (!open) return;
    if (aiPanel === 'notes' || notesFeedbackActive || queuedOpenNoteId) return;
    setAiPanel('ai');
  }, [open, aiPanel, notesFeedbackActive, queuedOpenNoteId, setAiPanel]);

  const showAi = aiPanel === 'ai';
  const notesScope =
    notesFeedbackActive || queuedOpenNoteId
      ? 'org'
      : incident?.id
        ? 'incident'
        : 'org';

  const chatName = session.dchat.name || 'PRATUS AI Chat';

  const selectSession = useCallback(
    (chat: ChatSessionSummary | Record<string, never>) => {
      dispatch(setCurrentSitrepId(undefined));
      dispatch(setSelectedDChat(chat));
    },
    [dispatch]
  );

  const renameSession = useCallback(
    async (id: string, name: string) => {
      try {
        await (
          updateChat as unknown as {
            mutateAsync: (v: unknown) => Promise<unknown>;
          }
        )
          // eslint-disable-next-line camelcase -- COMS payload field
          .mutateAsync({ dchat_id: id, name, archived: false });
        if (session.dchat.id === id) {
          dispatch(setSelectedDChat({ ...session.dchat, name }));
        }
      } catch (error) {
        observability.captureException(error, {
          where: 'chatV2.renameSession',
        });
      }
    },
    [updateChat, session.dchat, dispatch]
  );

  const deleteSessionById = useCallback(
    async (id: string) => {
      try {
        await (
          deleteChat as unknown as {
            mutateAsync: (v: unknown) => Promise<unknown>;
          }
        ).mutateAsync(id);
        if (session.dchat.id === id) selectSession({});
      } catch (error) {
        observability.captureException(error, {
          where: 'chatV2.deleteSession',
        });
      }
    },
    [deleteChat, session.dchat.id, selectSession]
  );

  const copyMessage = useCallback(
    (message: ChatMessage) =>
      void exportDocx(
        cleanMessageContent(message.content),
        'Clipboard',
        chatName
      ),
    [chatName]
  );
  const downloadMessage = useCallback(
    (message: ChatMessage) => void exportDocx([message], 'Download', chatName),
    [chatName]
  );

  const hint = !session.selectionsValid
    ? 'Finish configuring the selected data sources (e.g. choose a SITREP or at least one layer) to send.'
    : undefined;

  const streaming =
    !!session.streamtext &&
    (session.status === 'loading' ||
      session.status === 'ongoing' ||
      session.status === 'pending_confirmation');

  const emptyState = useMemo(
    () => (
      <EmptyState
        catalog={catalog}
        hasIncident={!!incident?.id}
        orgName={group?.group_name}
        onPickPrompt={(prompt) => session.setInput(prompt)}
      />
    ),
    [catalog, incident?.id, group?.group_name, session]
  );

  return (
    <div
      className="tw:relative tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:bg-background tw:text-foreground"
      data-testid="ai-drawer-body"
      data-tour="pratus-ai-drawer"
      onDragOver={(event) => {
        if (!showAi || !session.groupGuid || session.isAgentRunning) return;
        if (Array.from(event.dataTransfer.types).includes('Files')) {
          event.preventDefault();
        }
      }}
      onDrop={(event) => {
        if (event.defaultPrevented || !showAi || !session.groupGuid) return;
        const files = Array.from(event.dataTransfer.files ?? []);
        if (files.length === 0 || session.isAgentRunning) return;
        event.preventDefault();
        session.addFiles(files);
      }}
    >
      {resizeHandle}

      <header className="tw:flex tw:h-12 tw:shrink-0 tw:items-center tw:gap-1 tw:border-b tw:border-border tw:px-2">
        <h2 className="tw:sr-only">PRATUS AI Chat</h2>
        <AiPanelToggle value={aiPanel} onChange={setAiPanel} />
        <div className="tw:min-w-0 tw:flex-1" />
        {showAi ? (
          <ModeToggle
            mode={session.mode}
            onChange={session.setMode}
            disabled={session.isAgentRunning}
          />
        ) : null}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close PRATUS AI"
                data-testid="ai-close"
                onClick={session.close}
              />
            }
          >
            <XIcon />
          </TooltipTrigger>
          <TooltipContent className="dark">Close</TooltipContent>
        </Tooltip>
      </header>

      {showAi ? (
        <div className="tw:flex tw:h-10 tw:shrink-0 tw:items-center tw:border-b tw:border-border tw:px-2">
          <div className="tw:min-w-0 tw:flex-1">
            <SessionPicker
              sessions={chats.filter((c) => typeof c?.name === 'string')}
              isLoading={chatsLoading}
              currentId={session.dchat.id}
              currentName={session.dchat.name}
              onSelect={selectSession}
              onRename={renameSession}
              onDelete={deleteSessionById}
            />
          </div>
        </div>
      ) : null}

      {showAi ? (
        <>
          <MessageList
            messages={session.messages}
            persisted={!!session.dchat.id}
            sendMessageTimestamp={session.sendMessageTimestamp}
            running={session.isAgentRunning}
            stage={session.priorStatus}
            streaming={streaming}
            scrollToEndSignal={session.status === 'succeeded'}
            onRedo={session.redoMessage}
            onCopy={copyMessage}
            onDownload={downloadMessage}
            onCancel={session.dchat.id ? session.cancel : undefined}
            onFetchFileUrl={fetchFileUrl}
            emptyState={emptyState}
          />

          {hasPendingReview && reviewDismissed ? (
            <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:border-t tw:border-border tw:px-3 tw:py-2 tw:text-sm">
              <span className="tw:min-w-0 tw:flex-1 tw:text-muted-foreground">
                PRATUS AI is waiting for your approval.
              </span>
              <Button
                type="button"
                size="sm"
                onClick={() => setDismissedReview(null)}
              >
                Review changes
              </Button>
            </div>
          ) : null}

          <Composer
            value={session.input}
            onChange={session.setInput}
            onSubmit={session.submit}
            canSubmit={session.canSubmit}
            running={session.isAgentRunning}
            onCancel={session.cancel}
            hint={hint}
            commands={session.commands}
            selections={session.realtimeSelections}
            setSelections={session.setRealtimeSelections}
            incidentName={incident?.name}
            aiLayerPickMode={session.aiLayerPickMode}
            onToggleAiLayerPickMode={session.toggleAiLayerPickMode}
            onStartAiAreaSelect={session.startAiAreaSelect}
            aiAreaSelectActive={session.aiAreaSelectActive}
            onZoomToMapArea={session.zoomToMapArea}
            focusNonce={session.composerFocusNonce}
            draftLabel={composerDraftLabel ?? undefined}
            onClearDraft={clearComposerDraft}
            attachments={session.attachments}
            attachmentIndexStates={session.attachmentIndexStates}
            onAddFiles={session.groupGuid ? session.addFiles : undefined}
            onRemoveAttachment={session.removeAttachment}
          />

          {session.dchat.id && (
            <footer className="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:gap-1 tw:border-t tw:border-border tw:px-3 tw:py-1.5">
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Download chat as Word document"
                      onClick={() =>
                        void exportDocx(session.messages, 'Download', chatName)
                      }
                    />
                  }
                >
                  <FileDownIcon />
                </TooltipTrigger>
                <TooltipContent className="dark">Download .docx</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Copy chat to clipboard"
                      onClick={() => {
                        void exportDocx(
                          session.messages,
                          'Clipboard',
                          chatName
                        );
                        toast.success('Chat copied to clipboard.');
                      }}
                    />
                  }
                >
                  <ClipboardCopyIcon />
                </TooltipTrigger>
                <TooltipContent className="dark">
                  Copy to clipboard
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Delete session"
                      onClick={() => setConfirmDelete(true)}
                    />
                  }
                >
                  <Trash2Icon />
                </TooltipTrigger>
                <TooltipContent className="dark">Delete session</TooltipContent>
              </Tooltip>
            </footer>
          )}
        </>
      ) : (
        <div
          className="tw:min-h-0 tw:flex-1 tw:overflow-hidden tw:p-3"
          data-testid="ai-drawer-notes"
        >
          <NotesView
            scope={notesScope}
            incidentId={notesScope === 'incident' ? incident?.id : undefined}
            openNoteId={queuedOpenNoteId}
            onOpenNoteConsumed={consumeQueuedOpenNoteId}
          />
        </div>
      )}

      <ReviewQueueModal
        show={hasPendingReview && !reviewDismissed}
        confirmations={session.pendingConfirmations}
        askText={session.confirmationAskText}
        submitting={session.status === 'loading'}
        onSubmit={session.submitConfirmations}
        onClose={() => setDismissedReview(session.pendingConfirmations)}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this PRATUS AI Chat session?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setConfirmDelete(false);
                session.deleteSession();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const AiDrawer: React.FC<AiDrawerProps> = ({
  open,
  onClose,
  selectedDatetime,
}) => {
  const isMobile = useIsMobile();
  const { width, isResizing, startResize, onHandleKeyDown } = useDrawerWidth();

  if (isMobile) {
    return (
      <TooltipProvider>
        <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
          <SheetContent
            side="right"
            showCloseButton={false}
            className="dark global-chat-sidebar tw:w-full tw:gap-0 tw:p-0 tw:sm:max-w-full"
            data-testid="ai-drawer"
          >
            <SheetTitle className="tw:sr-only">PRATUS AI Chat</SheetTitle>
            <DrawerBody
              open={open}
              onClose={onClose}
              selectedDatetime={selectedDatetime}
            />
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    );
  }

  if (!open) return null;

  const handle = (
    // WAI-ARIA window-splitter: a focusable separator with aria-valuenow is the
    // canonical resizable-pane pattern, which jsx-a11y does not model.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize PRATUS AI drawer"
      aria-valuenow={width}
      tabIndex={0}
      onPointerDown={startResize}
      onKeyDown={onHandleKeyDown}
      data-testid="ai-drawer-resize-handle"
      className={cn(
        'tw:absolute tw:inset-y-0 tw:left-0 tw:z-10 tw:w-1.5 tw:-translate-x-1/2 tw:cursor-col-resize tw:rounded-full tw:transition-colors tw:hover:bg-primary/60 tw:focus-visible:bg-primary tw:focus-visible:outline-none',
        isResizing && 'tw:bg-primary'
      )}
    />
  );

  return (
    <TooltipProvider>
      <aside
        className="dark global-chat-sidebar tw:relative tw:h-full tw:shrink-0 tw:overflow-hidden tw:border-l tw:border-border"
        style={{ width }}
        data-testid="ai-drawer"
        aria-label="PRATUS AI Chat"
      >
        <DrawerBody
          open={open}
          onClose={onClose}
          selectedDatetime={selectedDatetime}
          resizeHandle={handle}
        />
      </aside>
    </TooltipProvider>
  );
};

export default AiDrawer;
